import { Injectable } from '@angular/core';
import {
  IAudit,
  IErpOrder,
  IInstitution,
  IIntegratedCare,
  IModel,
  IPatient,
  IPatientNote,
  ISingleOrder,
} from '@pia/pia.shared';
import {
  AttachmentDB,
  AuditDB,
  DoctorDB,
  ErpOrderDB,
  HospitalDB,
  IntegratedCareDB,
  NursingHomeDB,
  NursingServiceDB,
  PatientDB,
  PatientNotesDB,
  PharmacyDB,
  SingleOrderDB,
} from 'src/app/common/repository/databases';
import { AttachmentDto } from 'src/app/shared/models/attachment/attachment-dto.model';
import { QueryService } from 'src/app/shared/services/query/query.service';
import { PatientDownloadComponent } from 'src/app/patient/components/patient-download/patient-download.component';
import { ToastController, ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { EmergencyPatientDataSaver } from './emergency-patient-data-saver.service';
import makeDebug from 'src/makeDebug';

const debug = makeDebug('patient:emergency-data-resolver');

export enum DownloadStepStatus {
  Pending = 'pending',
  Active = 'active',
  Done = 'done',
}

export interface DownloadStep {
  label: string;
  status: DownloadStepStatus;
}

export interface DownloadState {
  progress: number;
  steps: DownloadStep[];
}

export enum DownloadStepIdentifier {
  Documents = 0,
  Attachments = 1,
  Notes = 2,
  IntegratedCares = 3,
  Orders = 4,
  ErpOrders = 5,
  PartnerRoles = 6,
  SaveLocal = 7,
}

@Injectable()
export class EmergencyDataResolver {
  constructor(
    private _queryService: QueryService,
    private _emergencyPatientDataSaver: EmergencyPatientDataSaver,
    private _toastController: ToastController,
    private _translate: TranslateService,
    private _modalCtrl: ModalController
  ) {}

  /**
   * Lädt alle Detaildaten eines Patienten herunter und speichert sie lokal. Existiert bereits
   * eine lokale Kopie mit einem `validToDate` in der Zukunft, wird nicht neu heruntergeladen —
   * nur die 48h-Gültigkeit wird verlängert (WAM-3878).
   *
   * @param patient Der herunterzuladende Patient
   * @param announceTemporaryAvailability Zeigt nach Erfolg den Hinweis auf die 48-stündige
   *   Verfügbarkeit unter „Alle Patienten". Für Patienten, die ohnehin über die Patientenliste
   *   erreichbar sind, ist der Hinweis irreführend und gehört abgeschaltet.
   * @throws Reicht Fehler unverändert an den Aufrufer weiter. Das Fortschrittsfenster wird in
   *   jedem Fall geschlossen — es läuft mit `backdropDismiss: false` und wäre sonst für den
   *   Nutzer nicht mehr wegzubekommen.
   */
  public async downloadPatient(patient: IPatient, announceTemporaryAvailability = true): Promise<void> {
    debug('patient download started', { patientId: patient?._id });

    if (await this.isStillValidLocally(patient._id)) {
      debug('patient download skipped, still valid locally', { patientId: patient._id });
      await this._emergencyPatientDataSaver.save({ patient });
    } else {
      await this.downloadPatientFully(patient);
    }

    if (!announceTemporaryAvailability) {
      return;
    }

    const toast = await this._toastController.create({
      message: `${patient.firstName} ${patient.lastName} ist ab jetzt für 48 Stunden unter "Alle Patienten" verfügbar`,
      duration: 7500,
      position: 'bottom',
      buttons: [
        {
          text: this._translate.instant('CLOSE'),
          role: 'cancel',
        },
      ],
    });
    await toast.present();
  }

  private async isStillValidLocally(patientId: string): Promise<boolean> {
    const existingPatient = await this._queryService.get<IPatient>(patientId, PatientDB);
    return Boolean(existingPatient?.validToDate) && new Date(existingPatient.validToDate).getTime() > Date.now();
  }

  private async downloadPatientFully(patient: IPatient): Promise<void> {
    const downloadState: DownloadState = {
      progress: 0,
      steps: [
        { label: 'Dokumente', status: DownloadStepStatus.Active },
        { label: 'Anhänge', status: DownloadStepStatus.Active },
        { label: 'Notizen', status: DownloadStepStatus.Active },
        { label: 'Versorgungsdaten', status: DownloadStepStatus.Active },
        { label: 'Verordnungen', status: DownloadStepStatus.Active },
        { label: 'ERP-Verordnungen', status: DownloadStepStatus.Active },
        { label: 'Partnerrollen', status: DownloadStepStatus.Pending },
        { label: 'Speichere Daten lokal', status: DownloadStepStatus.Pending },
      ],
    };

    const modal = await this._modalCtrl.create({
      component: PatientDownloadComponent,
      backdropDismiss: false,
      cssClass: 'patient-download-modal',
      componentProps: {
        state: downloadState,
        patient: patient,
      },
    });

    await modal.present();

    const markStepDone = (stepIdx: number) => {
      downloadState.steps[stepIdx].status = DownloadStepStatus.Done;
      const completedSteps = downloadState.steps.filter(step => step.status === DownloadStepStatus.Done).length;
      downloadState.progress = Math.round((completedSteps / downloadState.steps.length) * 100);
    };

    try {
      const patientData = await this.loadPatientData(patient, markStepDone);
      debug('patient download data loaded', { patientId: patient._id });
      downloadState.steps[DownloadStepIdentifier.PartnerRoles].status = DownloadStepStatus.Active;
      const institutionData = await this.resolveInstitutions(patient, markStepDone);
      debug('patient download institutions resolved', { patientId: patient._id });
      downloadState.steps[DownloadStepIdentifier.SaveLocal].status = DownloadStepStatus.Active;
      await this._emergencyPatientDataSaver.save({ ...patientData, patient, ...institutionData });
      debug('patient download saved locally', { patientId: patient._id });
      markStepDone(DownloadStepIdentifier.SaveLocal);
    } catch (error) {
      debug('patient download failed', { patientId: patient._id, error });
      throw error;
    } finally {
      await modal.dismiss();
      debug('patient download finished', { patientId: patient._id });
    }
  }

  public async loadPatientData(patient: IPatient, markStepDone?: (stepIdx: number) => void): Promise<any> {
    if (!patient) {
      throw new Error('EMERGENCYDATARESOLVER LOADPATIENTDATA / Patient must be valid to download data.');
    }

    const patientId = patient._id;

    const [documents, attachments, patientNotes, integratedCares, singleOrders, erpOrders] = await Promise.all([
      this.downloadPatientData<IAudit>(patientId, AuditDB).then(result => {
        markStepDone?.(DownloadStepIdentifier.Documents);
        return result;
      }),

      this.downloadPatientData<AttachmentDto>(patientId, AttachmentDB).then(result => {
        markStepDone?.(DownloadStepIdentifier.Attachments);
        return result;
      }),

      this.downloadPatientData<IPatientNote>(patientId, PatientNotesDB).then(result => {
        markStepDone?.(DownloadStepIdentifier.Notes);
        return result;
      }),

      this.downloadPatientData<IIntegratedCare>(patientId, IntegratedCareDB).then(result => {
        markStepDone?.(DownloadStepIdentifier.IntegratedCares);
        return result;
      }),

      this.downloadPatientData<ISingleOrder>(patientId, SingleOrderDB).then(result => {
        markStepDone?.(DownloadStepIdentifier.Orders);
        return result;
      }),

      this.downloadPatientData<IErpOrder>(patientId, ErpOrderDB).then(result => {
        markStepDone?.(DownloadStepIdentifier.ErpOrders);
        return result;
      }),
    ]);

    return {
      documents,
      attachments,
      patientNotes,
      integratedCares,
      singleOrders,
      erpOrders,
    };
  }

  public async resolveInstitutions(patient: IPatient, markStepDone?: (stepIdx: number) => void): Promise<any> {
    const hospitalId = patient.hospital?.hospitalId;

    const [doctor, hospital, nursingHome, nursingService, pharmacy] = await Promise.all([
      this.downloadInstitution(patient.primaryDoctorId, DoctorDB),
      this.downloadInstitution(hospitalId, HospitalDB),
      this.downloadInstitution(patient.nursingHomeId, NursingHomeDB),
      this.downloadInstitution(patient.nursingServiceId, NursingServiceDB),
      this.downloadInstitution(patient.pharmacyId, PharmacyDB),
    ]);

    markStepDone?.(DownloadStepIdentifier.PartnerRoles);

    return {
      doctor,
      hospital,
      nursingHome,
      nursingService,
      pharmacy,
    };
  }

  private downloadPatientData<T extends IModel>(patientId: string, database: string): Promise<T[]> {
    return this._queryService.search<T>({ query: `patientId:${patientId}` }, database, {
      ignoreRegionIds: true,
    });
  }

  private async downloadInstitution(institutionId: string, database: string): Promise<IInstitution> {
    if (!institutionId) {
      return;
    }

    return await this._queryService.get<IInstitution>(institutionId, database, {
      useOnlineDatabase: true,
    });
  }
}
